import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { DocumentColumnKey, DocumentsSort, EvidenceDocument } from '../documents.types'
import {
  COLUMN_TO_SORT_FIELD,
  OPTIONAL_COLUMN_DEFS,
  createActionsColumn,
  createTitleColumn,
} from './documents-table.columns'

type DocumentsTableProps = {
  documents: EvidenceDocument[]
  visibleColumns: DocumentColumnKey[]
  sort: DocumentsSort
  onSortChange: (sort: DocumentsSort) => void
  // Preserved on the Title link so opening a document keeps the list's filters/sort/page
  // intact. A real <Link>, not onClick, so right-click -> open in new tab works.
  search: string
  onEdit: (document: EvidenceDocument) => void
  onDelete: (document: EvidenceDocument) => void
  isLoading?: boolean
}

export const DocumentsTable = ({
  documents,
  visibleColumns,
  sort,
  onSortChange,
  search,
  onEdit,
  onDelete,
  isLoading,
}: DocumentsTableProps) => {
  const columns: ColumnDef<EvidenceDocument>[] = [
    createTitleColumn(search),
    ...visibleColumns.map((key) => OPTIONAL_COLUMN_DEFS[key]),
    createActionsColumn(search, onEdit, onDelete),
  ]

  const table = useReactTable({
    data: documents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  })

  const toggleSort = (columnId: string) => {
    const field = COLUMN_TO_SORT_FIELD[columnId]
    if (!field) return
    const nextDirection = sort.field === field && sort.direction === 'asc' ? 'desc' : 'asc'
    onSortChange({ field, direction: nextDirection })
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className="rounded-md border border-border p-8 text-center text-muted-foreground">
        No documents match your filters.
      </div>
    )
  }

  return (
    <div className="border rounded-lg overflow-hidden overflow-x-auto bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="bg-sidebar-accent">
              {hg.headers.map((header) => {
                const field = COLUMN_TO_SORT_FIELD[header.column.id]
                const isSorted = !!field && sort.field === field

                return (
                  <TableHead key={header.id} className="font-semibold">
                    {field ? (
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => toggleSort(header.column.id)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {isSorted && sort.direction === 'asc' && (
                          <ArrowUp className="h-3.5 w-3.5 flex-shrink-0" />
                        )}
                        {isSorted && sort.direction === 'desc' && (
                          <ArrowDown className="h-3.5 w-3.5 flex-shrink-0" />
                        )}
                        {!isSorted && (
                          <ArrowUpDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
